import * as Phaser from 'phaser';
import { perspectiveScale, type PerspectiveProfile } from '@game/shared';

// ── Types ────────────────────────────────────────────────────────────────────

export const enum WalkerMode {
  IDLE,
  AUTONOMOUS,
  PLAYER_CONTROLLED,
}

export const enum AutonomousPhase {
  WALKING_FORWARD,
  PAUSED,
  WALKING_BACK,
  OFF_SCREEN_WAIT,
}

export interface WalkerAnimationKeys {
  walkUp: string;
  walkDown: string;
  walkLeft: string;
  walkRight: string;
  idle: string;
  talk?: string;
}

export interface WalkerAnimationDef {
  key: string;
  textureKey: string;
  startFrame: number;
  endFrame: number;
  frameRate: number;
  repeat: number;
  flipX?: boolean;
}

export interface AutonomousConfig {
  waypoints: Array<{ x: number; y: number }>;
  speed: number;
  pauseDurationMs: number;
  offScreenWaitMs: number;
  pauseAnimKey?: string;
}

export interface WalkerAnimatorConfig {
  scene: Phaser.Scene;
  perspective: PerspectiveProfile;
  animKeys: WalkerAnimationKeys;
  animations: WalkerAnimationDef[];
  spriteTextureKey: string;
  spriteDepth: number;
  shadowDepth: number;
  initialAlpha?: number;
}

// ── Service ──────────────────────────────────────────────────────────────────

export class WalkerAnimator {
  readonly sprite: Phaser.GameObjects.Sprite;
  readonly shadow: Phaser.GameObjects.Ellipse;

  private scene: Phaser.Scene;
  private perspective: PerspectiveProfile;
  private animKeys: WalkerAnimationKeys;
  private currentAnim = '';
  private prevNX = 0;
  private prevNY = 0;

  private mode: WalkerMode = WalkerMode.IDLE;

  // Autonomous state
  private autoConfig: AutonomousConfig | null = null;
  private autoPhase: AutonomousPhase = AutonomousPhase.OFF_SCREEN_WAIT;
  private pathProgress = 0;
  private timer = 0;

  constructor(config: WalkerAnimatorConfig) {
    this.scene = config.scene;
    this.perspective = config.perspective;
    this.animKeys = config.animKeys;

    this.ensureAnimations(config.animations);

    const sprite = config.scene.add.sprite(0, 0, config.spriteTextureKey, 0);
    sprite.setOrigin(0.5, 1);
    sprite.setDepth(config.spriteDepth);
    sprite.setAlpha(config.initialAlpha ?? 1);
    this.sprite = sprite;

    const shadow = config.scene.add.ellipse(0, 0, 28, 10, 0x000000, 0.3);
    shadow.setOrigin(0.5, 0.5);
    shadow.setDepth(config.shadowDepth);
    shadow.setAlpha(config.initialAlpha ?? 1);
    this.shadow = shadow;
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  setPosition(nx: number, ny: number): void {
    const w = this.scene.scale.width;
    const h = this.scene.scale.height;
    this.sprite.setPosition(nx * w, ny * h);
    this.sprite.setScale(perspectiveScale(ny, this.perspective));
    this.prevNX = nx;
    this.prevNY = ny;
    this.syncShadow();
  }

  playIdle(): void {
    this.mode = WalkerMode.IDLE;
    this.playAnim(this.animKeys.idle);
  }

  startAutonomous(config: AutonomousConfig): void {
    this.autoConfig = config;
    this.mode = WalkerMode.AUTONOMOUS;
    this.autoPhase = AutonomousPhase.WALKING_FORWARD;
    this.pathProgress = 0;

    const start = config.waypoints[0];
    this.setPosition(start.x, start.y);
    this.playAnim(this.animKeys.walkUp);
  }

  stopAutonomous(): void {
    if (this.mode === WalkerMode.AUTONOMOUS) {
      this.mode = WalkerMode.IDLE;
      this.playAnim(this.animKeys.idle);
    }
  }

  setPlayerControlled(): void {
    this.mode = WalkerMode.PLAYER_CONTROLLED;
    this.playAnim(this.animKeys.idle);
  }

  getMode(): WalkerMode {
    return this.mode;
  }

  getAutoPhase(): AutonomousPhase {
    return this.autoPhase;
  }

  /**
   * Call from scene's update(). Handles autonomous path-follow and shadow sync.
   * For player-controlled mode, call updatePlayerMovement() instead.
   */
  update(delta: number): void {
    if (this.mode === WalkerMode.AUTONOMOUS) {
      this.tickAutonomous(delta);
    }
    this.syncShadow();
  }

  /**
   * Move the walker based on a movement delta (normalized coords).
   * Handles direction detection and animation switching.
   * Returns true if the walker actually moved.
   */
  applyMovement(nx: number, ny: number, moved: boolean, dx: number, dy: number): void {
    const w = this.scene.scale.width;
    const h = this.scene.scale.height;
    this.sprite.setPosition(nx * w, ny * h);
    this.sprite.setScale(perspectiveScale(ny, this.perspective));

    if (moved && (dx !== 0 || dy !== 0)) {
      this.updateDirection(nx, ny);
    } else if (!moved) {
      this.playAnim(this.animKeys.idle);
    }
    this.syncShadow();
  }

  setPerspective(profile: PerspectiveProfile): void {
    this.perspective = profile;
  }

  destroy(): void {
    this.sprite.destroy();
    this.shadow.destroy();
  }

  // ── Internal ───────────────────────────────────────────────────────────────

  private ensureAnimations(defs: WalkerAnimationDef[]): void {
    for (const def of defs) {
      if (this.scene.anims.exists(def.key)) continue;
      if (!this.scene.textures.exists(def.textureKey)) continue;
      this.scene.anims.create({
        key: def.key,
        frames: this.scene.anims.generateFrameNumbers(def.textureKey, {
          start: def.startFrame,
          end: def.endFrame,
        }),
        frameRate: def.frameRate,
        repeat: def.repeat,
      });
    }
  }

  private playAnim(key: string): void {
    if (key === this.currentAnim) return;
    if (!this.scene.anims.exists(key)) return;
    this.sprite.play(key);
    this.currentAnim = key;

    // Reset flipX unless explicitly set by direction detection
    if (key !== this.animKeys.walkRight) {
      this.sprite.flipX = false;
    }
  }

  private updateDirection(nx: number, ny: number): void {
    const dx = nx - this.prevNX;
    const dy = ny - this.prevNY;
    this.prevNX = nx;
    this.prevNY = ny;

    if (Math.abs(dx) < 0.0001 && Math.abs(dy) < 0.0001) return;

    if (Math.abs(dx) > Math.abs(dy) * 0.6) {
      if (dx < 0) {
        this.playAnim(this.animKeys.walkLeft);
        this.sprite.flipX = false;
      } else {
        this.playAnim(this.animKeys.walkRight);
        this.sprite.flipX = true;
      }
    } else if (dy < 0) {
      this.playAnim(this.animKeys.walkUp);
    } else {
      this.playAnim(this.animKeys.walkDown);
    }
  }

  private syncShadow(): void {
    if (this.sprite.alpha > 0) {
      this.shadow.setPosition(this.sprite.x, this.sprite.y);
      const s = this.sprite.scaleX * 0.9;
      this.shadow.setScale(s, s * 0.5);
      this.shadow.setAlpha(this.sprite.alpha);
    }
  }

  private tickAutonomous(delta: number): void {
    const cfg = this.autoConfig;
    if (!cfg) return;

    const w = this.scene.scale.width;
    const h = this.scene.scale.height;

    switch (this.autoPhase) {
      case AutonomousPhase.WALKING_FORWARD: {
        this.pathProgress += cfg.speed * (delta / 1000);
        if (this.pathProgress >= 1) {
          this.pathProgress = 1;
          this.autoPhase = AutonomousPhase.PAUSED;
          this.timer = cfg.pauseDurationMs;
          const pauseAnim = cfg.pauseAnimKey ?? this.animKeys.talk ?? this.animKeys.idle;
          this.playAnim(pauseAnim);
          this.sprite.flipX = false;
        }
        const pos = interpolateWaypoints(cfg.waypoints, this.pathProgress);
        this.sprite.setPosition(pos.x * w, pos.y * h);
        this.updateDirection(pos.x, pos.y);
        this.sprite.setScale(perspectiveScale(pos.y, this.perspective));
        break;
      }

      case AutonomousPhase.PAUSED: {
        this.timer -= delta;
        if (this.timer <= 0) {
          this.autoPhase = AutonomousPhase.WALKING_BACK;
          this.pathProgress = 1;
          this.playAnim(this.animKeys.walkDown);
          const pos = interpolateWaypoints(cfg.waypoints, 1);
          this.prevNX = pos.x;
          this.prevNY = pos.y;
        }
        break;
      }

      case AutonomousPhase.WALKING_BACK: {
        this.pathProgress -= cfg.speed * (delta / 1000);
        if (this.pathProgress <= 0) {
          this.pathProgress = 0;
          this.autoPhase = AutonomousPhase.OFF_SCREEN_WAIT;
          this.timer = cfg.offScreenWaitMs;
          this.sprite.setAlpha(0);
          this.shadow.setAlpha(0);
        }
        const pos = interpolateWaypoints(cfg.waypoints, this.pathProgress);
        this.sprite.setPosition(pos.x * w, pos.y * h);
        this.updateDirection(pos.x, pos.y);
        this.sprite.setScale(perspectiveScale(pos.y, this.perspective));
        break;
      }

      case AutonomousPhase.OFF_SCREEN_WAIT: {
        this.timer -= delta;
        if (this.timer <= 0) {
          this.autoPhase = AutonomousPhase.WALKING_FORWARD;
          this.pathProgress = 0;
          const pos = interpolateWaypoints(cfg.waypoints, 0);
          this.sprite.setPosition(pos.x * w, pos.y * h);
          this.sprite.setScale(perspectiveScale(pos.y, this.perspective));
          this.sprite.setAlpha(0.85);
          this.shadow.setAlpha(0.85);
          this.playAnim(this.animKeys.walkUp);
          this.sprite.flipX = false;
          this.prevNX = pos.x;
          this.prevNY = pos.y;
        }
        break;
      }
    }
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function interpolateWaypoints(
  waypoints: Array<{ x: number; y: number }>,
  progress: number,
): { x: number; y: number } {
  const n = waypoints.length - 1;
  if (n <= 0) return waypoints[0];
  const t = Math.max(0, Math.min(1, progress));
  const seg = t * n;
  const i = Math.min(Math.floor(seg), n - 1);
  const frac = seg - i;
  const a = waypoints[i];
  const b = waypoints[i + 1];
  return {
    x: a.x + (b.x - a.x) * frac,
    y: a.y + (b.y - a.y) * frac,
  };
}
